import React, { useCallback } from 'react';
import {
	makeStyles,
	createStyles,
	Theme,
} from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@material-ui/icons/PictureAsPdfOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import { useDropzone, FileRejection, DropzoneOptions } from 'react-dropzone';
import { FieldRenderProps } from 'react-final-form';

const DEFAULT_MAX_FILES = 50;
const DEFAULT_MAX_FILE_SIZE_MB = 15;
const DEFAULT_MAX_FILE_SIZE_B = DEFAULT_MAX_FILE_SIZE_MB * 1024 * 1024;

const DEFAULT_ACCEPTED_TYPES: DropzoneOptions['accept'] = {
	'application/pdf':                                                         ['.pdf'],
	'image/jpeg':                                                              ['.jpg', '.jpeg'],
	'image/png':                                                               ['.png'],
	'image/gif':                                                               ['.gif'],
	'image/webp':                                                              ['.webp'],
	'application/vnd.ms-excel':                                                ['.xls'],
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':       ['.xlsx'],
	'application/msword':                                                      ['.doc'],
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
	'text/plain':                                                              ['.txt'],
	'text/csv':                                                                ['.csv'],
};

export interface AttachedFile {
	uid:      string;
	file:     File | null;
	name:     string;
	url?:     string | null;
	guidID?:  string | null;
	status?:  number;
	preview?: string;
	errors:   string[];
}

type AttachmentsFieldProps = FieldRenderProps<AttachedFile[] | undefined, HTMLElement> & {
	maxFiles?: number;
	maxFileSizeBytes?: number;
	maxFileSizeMb?: number;
	// null = any file type (the extension chips are replaced by an "Any file type" note); leave it out for the default list
	acceptedTypes?: DropzoneOptions['accept'] | null;
	// a short one-line drop zone (icon and text side by side, no extension chips) for dialogs that are short on space
	compact?: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		dropzone: {
			border: `2px dashed ${theme.palette.divider}`,
			borderRadius: 10,
			padding: theme.spacing(4, 2),
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			gap: theme.spacing(1),
			cursor: 'pointer',
			transition: 'border-color 0.15s, background 0.15s',
			backgroundColor: theme.palette.grey[50],
			outline: 'none',
			userSelect: 'none',
		},

		dropzoneCompact: {
			flexDirection: 'row',
			justifyContent: 'flex-start',
			padding: theme.spacing(1, 2),
			gap: theme.spacing(1.5),
			'& $dropzoneUploadIcon': { fontSize: 24, marginBottom: 0 },
			'& $dropzoneTitle, & $dropzoneSub': { textAlign: 'left' },
			'& $dropzoneChips': { display: 'none' },
		},

		dropzoneCompactText: {
			display: 'flex',
			flexDirection: 'column',
		},

		dropzoneIdle: {
			'&:hover': {
				borderColor: theme.palette.primary.main,
				backgroundColor: '#f0f7ff',
			},
		},

		dropzoneActive: {
			borderColor: theme.palette.primary.main,
			backgroundColor: '#e3f2fd',
		},

		dropzoneRejected: {
			borderColor: theme.palette.error.main,
			backgroundColor: '#fff8f8',
		},

		dropzoneDisabled: {
			opacity: 0.45,
			cursor: 'not-allowed',
			pointerEvents: 'none',
		},

		dropzoneUploadIcon: {
			fontSize: 36,
			color: theme.palette.primary.light,
			marginBottom: theme.spacing(0.5),
		},

		dropzoneTitle: {
			fontSize: 13,
			fontWeight: 600,
			color: theme.palette.text.primary,
			textAlign: 'center',
		},

		dropzoneBrowse: {
			color: theme.palette.primary.main,
			textDecoration: 'underline',
		},

		dropzoneSub: {
			fontSize: 11,
			color: theme.palette.text.disabled,
			textAlign: 'center',
			lineHeight: 1.6,
		},

		dropzoneChips: {
			display: 'flex',
			flexWrap: 'wrap',
			justifyContent: 'center',
			gap: theme.spacing(0.5),
			marginTop: theme.spacing(0.5),
		},

		dropzoneChip: {
			fontSize: 10,
			fontWeight: 600,
			padding: '2px 7px',
			borderRadius: 20,
			backgroundColor: theme.palette.grey[200],
			color: theme.palette.text.secondary,
			letterSpacing: '0.03em',
		},

		formError: {
			fontSize: 11,
			color: theme.palette.error.main,
			marginTop: theme.spacing(1),
		},

		fileListHeader: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			marginTop: theme.spacing(2),
			marginBottom: theme.spacing(1),
		},

		fileListTitle: {
			fontSize: 11,
			fontWeight: 700,
			color: theme.palette.text.secondary,
			letterSpacing: '0.05em',
			textTransform: 'uppercase',
		},

		fileCountBadge: {
			fontSize: 11,
			fontWeight: 600,
			padding: '2px 8px',
			borderRadius: 20,
			backgroundColor: theme.palette.grey[100],
			color: theme.palette.text.secondary,
		},

		fileCountBadgeWarn: {
			backgroundColor: '#fff3e0',
			color: '#e65100',
		},

		fileList: {
			display: 'flex',
			flexDirection: 'column',
			gap: theme.spacing(0.75),
		},

		fileItem: {
			display: 'flex',
			alignItems: 'center',
			gap: theme.spacing(1.25),
			padding: theme.spacing(1, 1.25),
			borderRadius: 8,
			border: `1px solid ${theme.palette.divider}`,
			backgroundColor: '#fff',
			transition: 'border-color 0.12s',
		},

		fileItemError: {
			borderColor: theme.palette.error.light,
			backgroundColor: '#fff8f8',
		},

		fileIconBox: {
			width: 36,
			height: 36,
			borderRadius: 8,
			flexShrink: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: theme.palette.grey[100],
			'& .MuiSvgIcon-root': { fontSize: 20 },
		},

		fileThumb: {
			width: 36,
			height: 36,
			borderRadius: 8,
			objectFit: 'cover',
			flexShrink: 0,
			border: `1px solid ${theme.palette.divider}`,
		},

		fileMeta: {
			flex: 1,
			minWidth: 0,
		},

		fileName: {
			fontSize: 12,
			fontWeight: 600,
			color: theme.palette.text.primary,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},

		fileDetail: {
			fontSize: 11,
			color: theme.palette.text.disabled,
			marginTop: 1,
		},

		fileLink: {
			color: theme.palette.primary.main,
			cursor: 'pointer',
			textDecoration: 'underline',
		},

		fileActions: {
			display: 'flex',
			alignItems: 'center',
			gap: theme.spacing(0.5),
			flexShrink: 0,
		},

		fileErrorText: {
			fontSize: 11,
			color: theme.palette.error.main,
			marginTop: 1,
		},

		fileRemoveBtn: {
			padding: 4,
			flexShrink: 0,
			color: theme.palette.text.disabled,
			'& .MuiSvgIcon-root': { fontSize: 16 },
			'&:hover': {
				color: theme.palette.error.main,
				backgroundColor: '#ffebee',
			},
		},
	})
);

const formatBytes = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileName = (attachment: AttachedFile): string => attachment.file?.name ?? attachment.name;

const isImageAttachment = (attachment: AttachedFile) => {
	if (attachment.file?.type.startsWith('image/')) return true;

	return /\.(jpg|jpeg|png|gif|webp)$/i.test(getFileName(attachment));
};

const getAttachmentLink = (attachment: AttachedFile) => attachment.url ?? attachment.preview ?? null;

const getFileIcon = (attachment: AttachedFile) => {
	const fileName = getFileName(attachment);
	const fileType = attachment.file?.type ?? '';

	if (isImageAttachment(attachment)) return <ImageOutlinedIcon />;
	if (fileType === 'application/pdf' || /\.pdf$/i.test(fileName)) return <PictureAsPdfOutlinedIcon />;
	if (fileType.includes('word') || /\.(doc|docx)$/i.test(fileName)) return <DescriptionOutlinedIcon />;
	if (
		fileType.includes('excel') ||
		fileType.includes('spreadsheet') ||
		/\.(xls|xlsx|csv)$/i.test(fileName)
	) return <DescriptionOutlinedIcon />;
	return <InsertDriveFileOutlinedIcon />;
};

const getFileIconColor = (attachment: AttachedFile): string => {
	const fileName = getFileName(attachment);
	const fileType = attachment.file?.type ?? '';

	if (isImageAttachment(attachment)) return '#7b61ff';
	if (fileType === 'application/pdf' || /\.pdf$/i.test(fileName)) return '#e53935';
	if (fileType.includes('word') || /\.(doc|docx)$/i.test(fileName)) return '#1565c0';
	if (
		fileType.includes('excel') ||
		fileType.includes('spreadsheet') ||
		/\.(xls|xlsx|csv)$/i.test(fileName)
	) return '#2e7d32';
	return '#546e7a';
};

const mapRejectionErrors = (rejection: FileRejection, maxFiles: number, maxFileSizeMb: number): string[] =>
	rejection.errors.map(e => {
		switch (e.code) {
			case 'file-too-large':
				return `Exceeds ${maxFileSizeMb} MB limit.`;
			case 'file-invalid-type':
				return 'File type not supported.';
			case 'too-many-files':
				return `Maximum ${maxFiles} files allowed.`;
			default:
				return e.message;
		}
	});

const AttachmentsField: React.FC<AttachmentsFieldProps> = ({
	input: { value, onChange },
	meta,
	maxFiles = DEFAULT_MAX_FILES,
	maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE_B,
	maxFileSizeMb = DEFAULT_MAX_FILE_SIZE_MB,
	acceptedTypes = DEFAULT_ACCEPTED_TYPES,
	compact = false,
}) => {
	const classes = useStyles();
	const attachments: AttachedFile[] = value ?? [];
	const visibleAttachments = attachments.filter(attachment => attachment.status !== 2);

	const onDrop = useCallback((accepted: File[], rejected: FileRejection[]) => {
		const remainingSlots = maxFiles - visibleAttachments.length;

		// Only real validation failures (wrong type / too large) become error rows.
		// "Too many files" is never surfaced as an error — extras are just dropped silently.
		const realRejections = rejected.filter(r => r.errors.some(e => e.code !== 'too-many-files'));

		const acceptedFiles: AttachedFile[] = accepted
			.slice(0, remainingSlots)
			.map(file => ({
				uid: `${file.name}-${file.lastModified}-${Math.random()}`,
				file,
				name: file.name,
				url: null,
				guidID: null,
				status: 1,
				preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
				errors: [],
			}));

		const remainingAfterAccepted = remainingSlots - acceptedFiles.length;

		const rejectedFiles: AttachedFile[] = remainingAfterAccepted > 0
			? realRejections
				.slice(0, remainingAfterAccepted)
				.map(rejection => ({
					uid: `${rejection.file.name}-${rejection.file.lastModified}-${Math.random()}`,
					file: rejection.file,
					name: rejection.file.name,
					url: null,
					guidID: null,
					status: 1,
					errors: mapRejectionErrors(rejection, maxFiles, maxFileSizeMb).filter(Boolean),
				}))
			: [];

		onChange([...attachments, ...acceptedFiles, ...rejectedFiles]);
	}, [attachments, maxFileSizeMb, maxFiles, onChange, visibleAttachments.length]);

	const atLimit = visibleAttachments.length >= maxFiles;

	// No `maxFiles` here on purpose: with it set, react-dropzone rejects every file
	// (even valid ones) with a "too many files" error the moment the selection exceeds
	// the limit. We instead accept the selection and silently slice it down to the
	// remaining slots in onDrop above, so only genuine type/size errors are ever shown.
	const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
		onDrop,
		accept: acceptedTypes ?? undefined,
		maxSize: maxFileSizeBytes,
		disabled: atLimit,
	});

	const removeFile = (uid: string) => {
		const target = attachments.find(a => a.uid === uid);

		if (!target) return;

		if (target.guidID) {
			onChange(attachments.map(attachment => (
				attachment.uid === uid
					? { ...attachment, status: 2 }
					: attachment
			)));
			return;
		}

		if (target.preview) URL.revokeObjectURL(target.preview);
		onChange(attachments.filter(a => a.uid !== uid));
	};

	const validCount = visibleAttachments.filter(a => a.errors.length === 0).length;
	const invalidCount = visibleAttachments.filter(a => a.errors.length > 0).length;
	const showError = meta.touched && meta.error;

	const dropzoneTexts = (
		<>
						<Typography className={classes.dropzoneTitle}>
							{isDragActive && !isDragReject && 'Drop files here...'}
							{isDragReject && 'Some files will be rejected'}
							{!isDragActive && !atLimit && (
								<>Drag & drop files, or <span className={classes.dropzoneBrowse}>browse</span></>
							)}
							{atLimit && `Maximum of ${maxFiles} files reached`}
						</Typography>

						<Typography className={classes.dropzoneSub}>
							Up to {maxFiles} files · max {maxFileSizeMb} MB each
						</Typography>
		</>
	);

	const dropzoneClassName = [
		classes.dropzone,
		compact ? classes.dropzoneCompact : '',
		isDragReject ? classes.dropzoneRejected : '',
		isDragActive && !isDragReject ? classes.dropzoneActive : '',
		!isDragActive && !atLimit ? classes.dropzoneIdle : '',
		atLimit ? classes.dropzoneDisabled : '',
	].filter(Boolean).join(' ');

	return (
		<div>
			<div {...getRootProps({ className: dropzoneClassName })}>
				<input {...getInputProps()} />

				<CloudUploadOutlinedIcon className={classes.dropzoneUploadIcon} />

				{compact ? <div className={classes.dropzoneCompactText}>{dropzoneTexts}</div> : dropzoneTexts}

				<div className={classes.dropzoneChips}>
					{(acceptedTypes === null ? ['Any file type'] : ['PDF', 'JPG', 'PNG', 'XLSX', 'DOCX', 'CSV', 'TXT']).map(ext => (
						<span key={ext} className={classes.dropzoneChip}>{ext}</span>
					))}
				</div>
			</div>

			{showError && (
				<Typography className={classes.formError}>{meta.error}</Typography>
			)}

			{visibleAttachments.length > 0 && (
				<>
					<div className={classes.fileListHeader}>
						<Typography className={classes.fileListTitle}>
							{invalidCount > 0
								? `${invalidCount} file${invalidCount > 1 ? 's' : ''} could not be added`
								: `${validCount} file${validCount !== 1 ? 's' : ''} ready`}
						</Typography>
						<span className={[
							classes.fileCountBadge,
							atLimit ? classes.fileCountBadgeWarn : '',
						].join(' ')}>
							{visibleAttachments.length} / {maxFiles}
						</span>
					</div>

					<div className={classes.fileList}>
						{visibleAttachments.map(a => (
							<div
								key={a.uid}
								className={[
									classes.fileItem,
									a.errors.length > 0 ? classes.fileItemError : '',
								].join(' ')}
							>
								{a.preview ? (
									<img
										src={a.preview}
										alt={getFileName(a)}
										className={classes.fileThumb}
									/>
								) : (
									<div
										className={classes.fileIconBox}
										style={{ color: getFileIconColor(a) }}
									>
										{getFileIcon(a)}
									</div>
								)}

								<div className={classes.fileMeta}>
									{getAttachmentLink(a) ? (
										<Typography
											className={[classes.fileName, classes.fileLink].join(' ')}
											title={getFileName(a)}
											component="a"
											href={getAttachmentLink(a) ?? undefined}
											target="_blank"
											rel="noopener noreferrer"
										>
											{getFileName(a)}
										</Typography>
									) : (
										<Typography className={classes.fileName} title={getFileName(a)}>
											{getFileName(a)}
										</Typography>
									)}
									{a.errors.length > 0 ? (
										a.errors.map((err, i) => (
											<Typography key={i} className={classes.fileErrorText}>
												{err}
											</Typography>
										))
									) : (
										<Typography className={classes.fileDetail}>
											{a.file ? formatBytes(a.file.size) : 'Saved attachment'}
										</Typography>
									)}
								</div>

								<div className={classes.fileActions}>
									{getAttachmentLink(a) && (
										<Tooltip title="Download" placement="top">
											<IconButton
												className={classes.fileRemoveBtn}
												size="small"
												component="a"
												href={getAttachmentLink(a) ?? undefined}
												target="_blank"
												rel="noopener noreferrer"
												download={getFileName(a)}
											>
												<GetAppOutlinedIcon />
											</IconButton>
										</Tooltip>
									)}

									<Tooltip title="Remove" placement="top">
										<IconButton
											className={classes.fileRemoveBtn}
											size="small"
											onClick={() => removeFile(a.uid)}
										>
											<DeleteOutlineIcon />
										</IconButton>
									</Tooltip>
								</div>
							</div>
						))}
					</div>
				</>
			)}
		</div>
	);
};

export default AttachmentsField;
